export function transformMonthOverMonth(combinedReports) {
  const months = Object.keys(combinedReports).sort();

  const mergeNodes = (monthData, target, monthKey) => {
    const {
      name,
      amount,
      children = [],
      quickbooksItemId = -1,
      companyId = null,
    } = monthData;

    if (!target[name]) {
      target[name] = {
        name,
        months: {},
        children: {},
        quickbooksItemId,
        companyId,
      };
    }

    target[name].months[monthKey] = amount ?? 0;

    // Preserve first valid QB ID only
    if (
      target[name].quickbooksItemId === -1 &&
      quickbooksItemId !== -1
    ) {
      target[name].quickbooksItemId = quickbooksItemId;
      target[name].companyId = companyId;
    }

    for (const child of children) {
      mergeNodes(child, target[name].children, monthKey);
    }
  };

  const merged = {};
  for (const monthKey of months) {
    const root = combinedReports[monthKey];
    mergeNodes(root, merged, monthKey);
  }

  const convertToArray = (nodeDict) =>
    Object.values(nodeDict).map((node) => ({
      name: node.name,
      months: node.months,
      quickbooksItemId: node.quickbooksItemId,
      companyId: node.companyId,
      children: convertToArray(node.children),
    }));

  return convertToArray(merged);
}

export function normalizeResponse(result, monthOverMonth, transformFn, reportType = "Report") {
  if (!result?.data) return [];

  if (result?.data?.combinedReports) {
    const merged = transformFn(result.data.combinedReports);
    const months = Object.keys(result.data.combinedReports).sort();
    return [{ name: reportType, data: merged, months }];
  }

  if (typeof result.data === "object" && !Array.isArray(result.data)) {
    return Object.entries(result.data).map(([companyName, reportData]) => ({
      name: `${reportType} – ${companyName}`,
      data: Array.isArray(reportData) ? reportData : [],
      months: monthOverMonth ? Object.keys(reportData?.months || {}) : [],
    }));
  }

  if (Array.isArray(result.data)) {
    return [{ name: reportType, data: result.data, months: [] }];
  }

  return [];
}

/* ===============================
   Month-over-Month helpers
   =============================== */

/**
 * Returns the numeric total for a node.
 * Works for both MoM and non-MoM reports.
 */
export const getNodeTotalAmount = (node) => {
  if (!node) return 0;

  // Month-over-month nodes
  if (node.months && typeof node.months === "object") {
    return Object.values(node.months).reduce(
      (sum, val) => sum + (Number(val) || 0),
      0
    );
  }

  // Normal reports
  return Number(node.rawAmount ?? node.amount ?? 0);
};

/**
 * Normalizes amounts so every node has:
 * - rawAmount (single numeric value)
 * - displayAmount (initially same as rawAmount)
 */
export const normalizeAmounts = (node) => {
  const rawAmount = getNodeTotalAmount(node);

  return {
    ...node,
    rawAmount,
    displayAmount: rawAmount,
  };
};
